import React, { useState, useEffect } from "react";
import { codingStats as mockStats } from "../data/mock";
import { useInView } from "../hooks/useInView";
import { Trophy, Target, Flame } from "lucide-react";
import {
  fetchCodeforcesStats,
  fetchLeetCodeStats,
} from "../lib/codingStatsApi";

const AnimatedNumber = ({ value, isInView, duration = 1800 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration, isInView]);

  return <span>{display.toLocaleString()}</span>;
};

const RatingGraph = ({ data, isInView }) => {
  const width = 800;
  const height = 280;
  const pad = { top: 24, right: 24, bottom: 40, left: 52 };
  const cW = width - pad.left - pad.right;
  const cH = height - pad.top - pad.bottom;

  if (!data || data.length === 0) return null;

  const ratings = data.map((d) => d.rating);
  const dataMin = Math.min(...ratings);
  const dataMax = Math.max(...ratings);
  const rangePad = Math.max((dataMax - dataMin) * 0.25, 80);
  const minR = Math.floor((dataMin - rangePad) / 100) * 100;
  const maxR = Math.ceil((dataMax + rangePad) / 100) * 100;

  const points = data.map((d, i) => ({
    x: pad.left + (i / (data.length - 1)) * cW,
    y: pad.top + cH - ((d.rating - minR) / (maxR - minR)) * cH,
    rating: d.rating,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const areaPath = `${linePath} L${points[points.length - 1].x},${pad.top + cH} L${points[0].x},${pad.top + cH} Z`;

  const step = Math.max(Math.round((maxR - minR) / 5 / 100) * 100, 100);
  const gridLines = [];
  for (let r = Math.ceil(minR / step) * step; r <= maxR; r += step) {
    gridLines.push(r);
  }

  const bands = [
    { min: 0, max: 1200, fill: "#f7f7f7" },
    { min: 1200, max: 1400, fill: "#f0f7f0" },
    { min: 1400, max: 1600, fill: "#edf7f7" },
    { min: 1600, max: 1900, fill: "#edf2fa" },
    { min: 1900, max: 4000, fill: "#f5edf7" },
  ]
    .filter((b) => b.max > minR && b.min < maxR)
    .map((b) => ({
      ...b,
      min: Math.max(b.min, minR),
      max: Math.min(b.max, maxR),
    }));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      {bands.map((b) => {
        const y1 = pad.top + cH - ((b.max - minR) / (maxR - minR)) * cH;
        const y2 = pad.top + cH - ((b.min - minR) / (maxR - minR)) * cH;
        return (
          <rect
            key={b.min}
            x={pad.left}
            y={y1}
            width={cW}
            height={y2 - y1}
            fill={b.fill}
          />
        );
      })}

      {gridLines.map((r) => {
        const y = pad.top + cH - ((r - minR) / (maxR - minR)) * cH;
        return (
          <g key={r}>
            <line
              x1={pad.left}
              y1={y}
              x2={pad.left + cW}
              y2={y}
              stroke="#e8e8e8"
              strokeWidth="0.5"
              strokeDasharray="4 4"
            />
            <text
              x={pad.left - 10}
              y={y + 3.5}
              textAnchor="end"
              fill="#bbb"
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: "9px",
              }}
            >
              {r}
            </text>
          </g>
        );
      })}

      <defs>
        <linearGradient id="cfAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0" />
        </linearGradient>
      </defs>

      <path
        d={areaPath}
        fill="url(#cfAreaGrad)"
        className={`transition-opacity duration-1000 ${isInView ? "opacity-100" : "opacity-0"}`}
      />
      <path
        d={linePath}
        fill="none"
        stroke="#0a0a0a"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`transition-opacity duration-1000 delay-200 ${isInView ? "opacity-100" : "opacity-0"}`}
      />

      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="3"
          fill="white"
          stroke="#0a0a0a"
          strokeWidth="1.5"
          className={`transition-all duration-500 ${isInView ? "opacity-100" : "opacity-0"}`}
          style={{ transitionDelay: `${300 + i * 40}ms` }}
        />
      ))}

      {points.length > 0 && (
        <text
          x={points[points.length - 1].x}
          y={points[points.length - 1].y - 14}
          textAnchor="middle"
          fill="#0a0a0a"
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "11px",
            fontWeight: 600,
          }}
          className={`transition-opacity duration-700 delay-700 ${isInView ? "opacity-100" : "opacity-0"}`}
        >
          {points[points.length - 1].rating}
        </text>
      )}
    </svg>
  );
};

const LeetCodeRing = ({ solved, total, label, color, isInView }) => {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (isInView ? solved / total : 0));

  return (
    <div className="flex gap-4 items-center">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth="5"
        />
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform="rotate(-90 36 36)"
          className="transition-all duration-[1200ms] ease-out"
        />
        <text
          x="36"
          y="40"
          textAnchor="middle"
          fill="#0a0a0a"
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          {isInView ? solved : 0}
        </text>
      </svg>
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-[11px] text-gray-300 font-mono">
          {solved}/{total}
        </p>
      </div>
    </div>
  );
};

const CodingStatsSection = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.3 });
  const [statsRef, statsInView] = useInView({ threshold: 0.2 });
  const [graphRef, graphInView] = useInView({ threshold: 0.15 });
  const [lcRef, lcInView] = useInView({ threshold: 0.2 });

  const [codeforces, setCodeforces] = useState(mockStats.codeforces);
  const [leetcode, setLeetcode] = useState(mockStats.leetcode);
  const [totalQuestionsSolved, setTotal] = useState(
    mockStats.totalQuestionsSolved,
  );

  useEffect(() => {
    const cfHandle = mockStats.codeforces.handle;
    const lcHandle = mockStats.leetcode.handle;
    const load = async () => {
      const [cfData, lcData] = await Promise.all([
        fetchCodeforcesStats(cfHandle),
        fetchLeetCodeStats(lcHandle),
      ]);
      if (cfData) setCodeforces((prev) => ({ ...prev, ...cfData }));
      if (lcData) setLeetcode((prev) => ({ ...prev, ...lcData }));
    };
    load();
  }, []);

  useEffect(() => {
    const cfSolved =
      codeforces.totalSolved || codeforces.ratingHistory?.length * 5 || 0;
    setTotal((leetcode.totalSolved || 0) + cfSolved);
  }, [codeforces, leetcode]);

  return (
    <section id="coding" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div
          ref={headerRef}
          className={`mb-16 transition-all duration-700 ${headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-4">
            Competitive Programming
          </p>
          <h2 className="font-heading text-4xl md:text-[3.25rem] font-[200] tracking-tight leading-tight">
            Coding Stats<span className="text-gray-200">.</span>
          </h2>
        </div>

        {/* Stats Banner */}
        <div
          ref={statsRef}
          className={`mb-14 flex flex-wrap items-end gap-10 transition-all duration-700 ${statsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div>
            <p className="font-heading text-6xl md:text-7xl font-[200] tracking-tight">
              <AnimatedNumber value={893} isInView={statsInView} />
            </p>
            <p className="font-mono text-[11px] text-gray-300 mt-2 uppercase tracking-widest">
              Total Problems Solved
            </p>
          </div>
          <div className="h-14 w-[1px] bg-gray-200 hidden md:block" />
          <div className="flex flex-wrap gap-10">
            <div>
              <p className="font-heading text-3xl font-[300] tracking-tight">
                <AnimatedNumber
                  value={codeforces.contestsParticipated}
                  isInView={statsInView}
                />
              </p>
              <p className="font-mono text-[11px] text-gray-300 mt-1">
                CF Contests
              </p>
            </div>
            <div>
              <p className="font-heading text-3xl font-[300] tracking-tight">
                <AnimatedNumber
                  value={leetcode.ranking}
                  isInView={statsInView}
                />
              </p>
              <p className="font-mono text-[11px] text-gray-300 mt-1">
                LC Ranking
              </p>
            </div>
            <div>
              <p className="font-heading text-3xl font-[300] tracking-tight">
                {codeforces.rank}
              </p>
              <p className="font-mono text-[11px] text-gray-300 mt-1">
                CF Rank
              </p>
            </div>
          </div>
        </div>

        {/* Codeforces  Graph */}
        <div
          ref={graphRef}
          className={`mb-8 p-6 md:p-8 rounded-3xl bg-white border border-gray-100 transition-all duration-700 ${graphInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-3 items-center">
              <Trophy size={17} className="text-gray-400" strokeWidth={1.5} />
              <div>
                <h3 className="text-sm font-medium">Codeforces Rating</h3>
                <p className="font-mono text-[11px] text-gray-300">
                  @{codeforces.handle}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold font-heading">
                {codeforces.currentRating}
              </p>
              <p className="font-mono text-[11px] text-gray-300">
                max: {codeforces.maxRating}
              </p>
            </div>
          </div>
          <RatingGraph data={codeforces.ratingHistory} isInView={graphInView} />
        </div>

        {/* LeetCode Stats */}
        <div
          ref={lcRef}
          className={`p-6 md:p-8 rounded-3xl bg-white border border-gray-100 transition-all duration-700 delay-100 ${lcInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-3 items-center">
              <Target size={17} className="text-gray-400" strokeWidth={1.5} />
              <div>
                <h3 className="text-sm font-medium">LeetCode Progress</h3>
                <p className="font-mono text-[11px] text-gray-300">
                  @{leetcode.handle}
                </p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Flame size={14} className="text-gray-400" />
              <span className="font-mono text-[12px] text-gray-500">
                {leetcode.streak} day streak
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <LeetCodeRing
              solved={leetcode.easySolved}
              total={leetcode.totalQuestions.easy}
              label="Easy"
              color="#86efac"
              isInView={lcInView}
            />
            <LeetCodeRing
              solved={leetcode.mediumSolved}
              total={leetcode.totalQuestions.medium}
              label="Medium"
              color="#fde68a"
              isInView={lcInView}
            />
            <LeetCodeRing
              solved={leetcode.hardSolved}
              total={leetcode.totalQuestions.hard}
              label="Hard"
              color="#fca5a5"
              isInView={lcInView}
            />
          </div>

          <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-50">
            <span className="text-sm text-gray-400">Total Solved</span>
            <span className="text-2xl font-semibold font-heading">
              {leetcode.totalSolved}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CodingStatsSection;
